/**
 * Financial Service
 * Handles all financial data and transaction API operations
 * 
 * @module services/financialService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Financial Service Class
 * Provides methods for transactions, revenue analytics, profit tracking, and cash flow
 * 
 * @class FinancialService
 * @extends BaseService
 * 
 * @example
 * import financialService from './services/financialService';
 * 
 * // Get revenue analytics
 * const revenue = await financialService.getRevenueAnalytics({ period: 'month' });
 * 
 * // Record transaction
 * await financialService.createTransaction({
 *   type: 'income',
 *   amount: 5000000,
 *   category: 'sales'
 * });
 */
class FinancialService extends BaseService {
  constructor() {
    super('/financial');
  }

  /**
   * Get all transactions with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.type] - Filter by type (income, expense)
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.status] - Filter by status (pending, completed, cancelled)
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getTransactions(params = {}) {
    return this.get('/transactions', { params });
  }

  /**
   * Get transaction by ID
   * 
   * @param {string} transactionId - Transaction ID
   * @returns {Promise<Object>} Transaction details
   */
  async getTransactionById(transactionId) {
    return this.get(`/transactions/${transactionId}`);
  }

  /**
   * Create new transaction
   * 
   * @param {Object} transactionData - Transaction data
   * @param {string} transactionData.type - Transaction type (income, expense)
   * @param {number} transactionData.amount - Amount in GNF
   * @param {string} transactionData.category - Category
   * @param {string} transactionData.date - Transaction date
   * @param {string} [transactionData.description] - Description
   * @param {string} [transactionData.reference] - Reference number
   * @param {string} [transactionData.paymentMethod] - Payment method
   * @param {Object} [transactionData.metadata] - Additional metadata
   * @returns {Promise<Object>} Created transaction
   */
  async createTransaction(transactionData) {
    return this.post('/transactions', transactionData);
  }

  /**
   * Update transaction
   * 
   * @param {string} transactionId - Transaction ID
   * @param {Object} transactionData - Updated transaction data
   * @returns {Promise<Object>} Updated transaction
   */
  async updateTransaction(transactionId, transactionData) {
    return this.put(`/transactions/${transactionId}`, transactionData);
  }

  /**
   * Delete transaction
   * 
   * @param {string} transactionId - Transaction ID
   * @returns {Promise<void>}
   */
  async deleteTransaction(transactionId) {
    return this.delete(`/transactions/${transactionId}`);
  }

  /**
   * Get revenue analytics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period (day, week, month, quarter, year)
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.groupBy] - Group by (category, date, paymentMethod)
   * @returns {Promise<Object>} Revenue analytics data
   */
  async getRevenueAnalytics(params = {}) {
    return this.get('/analytics/revenue', { params });
  }

  /**
   * Get expense analytics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.groupBy] - Group by field
   * @returns {Promise<Object>} Expense analytics data
   */
  async getExpenseAnalytics(params = {}) {
    return this.get('/analytics/expenses', { params });
  }

  /**
   * Get profit and loss statement
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} params.startDate - Start date (required)
   * @param {string} params.endDate - End date (required)
   * @param {string} [params.format='summary'] - Format (summary, detailed)
   * @returns {Promise<Object>} P&L statement
   */
  async getProfitLoss(params) {
    return this.get('/reports/profit-loss', { params });
  }

  /**
   * Get cash flow statement
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @returns {Promise<Object>} Cash flow data
   */
  async getCashFlow(params = {}) {
    return this.get('/reports/cash-flow', { params });
  }

  /**
   * Get balance sheet
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.asOfDate] - Date for balance sheet
   * @returns {Promise<Object>} Balance sheet data
   */
  async getBalanceSheet(params = {}) {
    return this.get('/reports/balance-sheet', { params });
  }

  /**
   * Get profit margin analysis
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @param {string} [params.category] - Filter by category
   * @returns {Promise<Object>} Profit margin data
   */
  async getProfitMargins(params = {}) {
    return this.get('/analytics/profit-margins', { params });
  }

  /**
   * Get financial trends
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.metric] - Metric to analyze (revenue, expenses, profit, cashFlow)
   * @param {string} [params.period='month'] - Time period
   * @param {number} [params.periods=12] - Number of periods to include
   * @returns {Promise<Object>} Trend data
   */
  async getTrends(params = {}) {
    return this.get('/analytics/trends', { params });
  }

  /**
   * Get financial forecasts
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.metric] - Metric to forecast
   * @param {number} [params.forecastPeriods=6] - Periods to forecast
   * @param {string} [params.method='linear'] - Forecast method
   * @returns {Promise<Object>} Forecast data
   */
  async getForecast(params = {}) {
    return this.get('/analytics/forecast', { params });
  }

  /**
   * Get transactions by category
   * 
   * @param {string} category - Category name
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Transactions in category
   */
  async getByCategory(category, params = {}) {
    return this.get('/transactions/by-category', { params: { category, ...params } });
  }

  /**
   * Get transactions by payment method
   * 
   * @param {string} paymentMethod - Payment method
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Transactions by payment method
   */
  async getByPaymentMethod(paymentMethod, params = {}) {
    return this.get('/transactions/by-payment-method', { params: { paymentMethod, ...params } });
  }

  /**
   * Get account receivables
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.status] - Filter by status (pending, overdue, paid)
   * @returns {Promise<Array>} Receivables
   */
  async getReceivables(params = {}) {
    return this.get('/receivables', { params });
  }

  /**
   * Get account payables
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.status] - Filter by status (pending, overdue, paid)
   * @returns {Promise<Array>} Payables
   */
  async getPayables(params = {}) {
    return this.get('/payables', { params });
  }

  /**
   * Reconcile transactions
   * 
   * @param {Object} reconciliationData - Reconciliation data
   * @param {Array<string>} reconciliationData.transactionIds - Transaction IDs to reconcile
   * @param {string} reconciliationData.date - Reconciliation date
   * @param {Object} [reconciliationData.metadata] - Additional metadata
   * @returns {Promise<Object>} Reconciliation result
   */
  async reconcile(reconciliationData) {
    return this.post('/reconcile', reconciliationData);
  }

  /**
   * Get financial dashboard summary
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Dashboard summary data
   */
  async getDashboardSummary(params = {}) {
    return this.get('/dashboard/summary', { params });
  }

  /**
   * Get key financial metrics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Key metrics
   */
  async getKeyMetrics(params = {}) {
    return this.get('/metrics', { params });
  }

  /**
   * Get financial alerts
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.severity] - Filter by severity (critical, warning, info)
   * @param {boolean} [params.unread] - Only unread alerts
   * @returns {Promise<Array>} Financial alerts
   */
  async getAlerts(params = {}) {
    return this.get('/alerts', { params });
  }

  /**
   * Export financial data
   * 
   * @param {Object} params - Export parameters
   * @param {string} params.reportType - Report type (transactions, revenue, expenses, profitLoss, cashFlow)
   * @param {string} [params.format='xlsx'] - Export format (csv, xlsx, pdf)
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @param {Object} [params.filters] - Additional filters
   * @returns {Promise<Blob>} Export file
   */
  async export(params) {
    return this.get('/export', {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Import transactions from file
   * 
   * @param {File} file - CSV or Excel file
   * @param {Object} [options={}] - Import options
   * @param {boolean} [options.validateDuplicates=true] - Check for duplicates
   * @param {boolean} [options.skipErrors=true] - Skip rows with errors
   * @returns {Promise<{imported: number, skipped: number, failed: number, errors: Array}>}
   */
  async import(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return this.post('/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Get financial statistics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Financial statistics
   */
  async getStatistics(params = {}) {
    return this.get('/statistics', { params });
  }

  /**
   * Validate transaction reference
   * 
   * @param {string} reference - Reference to validate
   * @param {string} [excludeTransactionId] - Transaction ID to exclude (for updates)
   * @returns {Promise<{available: boolean}>}
   */
  async validateReference(reference, excludeTransactionId = null) {
    return this.get('/validate-reference', {
      params: { reference, excludeTransactionId }
    });
  }
}

// Export singleton instance
const financialService = new FinancialService();
export default financialService;
